/* Device finder: match an Apple model number (or a device name, "iPhone 15") against the `device` metaobjects and
 * show the products that fit. */
(() => {
  const configEl = document.querySelector('[data-device-finder-config]');
  const form = document.querySelector('[data-device-finder-form]');
  if (!configEl || !form) return;

  let config;
  try { config = JSON.parse(configEl.textContent); } catch (e) { return; }

  const T = window.Theme || {};
  const S = config.strings || {};
  /* `t` HTML-escapes the strings (doesn&#39;t): decode the ones shown as text */
  const decode = (v) => { const ta = document.createElement('textarea'); ta.innerHTML = v; return ta.value; };
  Object.keys(S).forEach((k) => { if (!/HTML?$|chooseSize|buyConfidence/.test(k)) S[k] = decode(S[k]); });
  const status = document.querySelector('[data-device-finder-status]');
  const results = document.querySelector('[data-device-finder-results]');
  const grid = document.querySelector('[data-device-finder-grid]');
  const input = form.querySelector('input[name="model"]');
  const root = String(config.root || '/').replace(/\/$/, '');
  const norm = (v) => String(v || '').toUpperCase().replace(/\s+/g, '');

  const index = new Map();
  (config.devices || []).forEach((d) => (d.models || []).forEach((m) => { if (m) index.set(norm(m), d); }));

  const el = (tag, attrs = {}, children = []) => {
    const n = document.createElement(tag);
    for (const [k, v] of Object.entries(attrs)) {
      if (v == null || v === false) continue;
      if (k === 'class') n.className = v;
      else if (k === 'text') n.textContent = v;
      else if (k === 'html') n.innerHTML = v; // translated theme strings only
      else n.setAttribute(k, v === true ? '' : v);
    }
    [].concat(children).forEach((c) => c && n.append(c));
    return n;
  };

  // Rendered collection card (+ lifestyle tile) for a product, in the visitor's market and language.
  const fetchCard = (handle) =>
    fetch(`${root}/products/${encodeURIComponent(handle)}?view=finder-card`)
      .then((r) => (r.ok ? r.text() : Promise.reject(r.status)))
      .then((html) => {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const card = doc.querySelector('.collection-grid__item');
        if (!card) return Promise.reject(new Error('no card'));
        const life = doc.querySelector('template[data-lifestyle]')?.content.querySelector('li') || null;
        return { card, life };
      });

  // Each search bumps this; a run that finds it changed after its await was overtaken and stops.
  let runId = 0;

  /* By name: every word typed must be a word of the device name ("iphone 15" → "iPhone 15 / 15 Pro" and
   * "iPhone 15 Pro Max / 15 Plus"); exact names win. */
  const words = (v) => String(v || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/(\d)"/g, '$1 ').match(/[a-z]+|\d+(?:\.\d+)?/g) || [];
  const byName = (raw) => {
    const q = words(raw);
    if (!q.length || !q.some((w) => /[a-z]/.test(w))) return [];
    const k = q.join(' ');
    const list = (config.devices || []).filter((d) => { const n = words(d.name); return q.every((w) => n.includes(w)); });
    const exact = list.filter((d) => words(d.name.replace(/\([^)]*\)/g, '')).join(' ') === k);
    return exact.length === 1 ? exact : list;
  };

  async function search(raw, picked) {
    const model = norm(raw);
    if (!model) return;
    const run = ++runId;
    let device = picked || index.get(model) || index.get(model.replace(/^(?!A)/, 'A'));
    if (!device) {
      const named = byName(raw);
      if (named.length === 1) [device] = named;
      else if (named.length > 1) {
        status.replaceChildren();
        grid.replaceChildren();
        results.hidden = true;
        const list = el('ul', { class: 'device-finder__choices', role: 'list' });
        named.slice(0, 12).forEach((d) => {
          const b = el('button', { type: 'button', class: 'btn btn--secondary btn--sm', text: d.name });
          b.addEventListener('click', () => search(raw, d));
          list.append(el('li', {}, [b]));
        });
        status.append(el('h3', { text: S.whichOne }), list);
        return;
      }
    }
    status.replaceChildren();
    grid.replaceChildren();
    results.hidden = true;

    const url = new URL(location.href);
    url.searchParams.set('model', model);
    history.replaceState(history.state, '', url);

    if (!device) {
      status.append(el('h3', { text: S.noMatch }), el('p', { text: S.doubleCheck }), el('p', { text: S.nameHint }));
      document.dispatchEvent(new CustomEvent('finder:miss', { detail: { model } })); // Insights: devices we have no sleeve for
      return;
    }

    // Remember it like the home band does (product pages mark the size that fits; theme.js sends it to Klaviyo)
    try {
      const k = device.name.toLowerCase().replace(/[^a-z0-9]/g, '');
      const family = ['macbook', 'ipad', 'iphone', 'kindle', 'airtag'].find((f) => k.startsWith(f)) || null;
      localStorage.setItem('harber:device', JSON.stringify({ name: device.name, models: device.models || [model], handles: device.handles || [], family }));
    } catch { /* private mode */ }

    const heading = el('h3', {}, [document.createTextNode(`${S.youMustChoose} `), el('span', { class: 'device-finder__model', text: device.name })]);
    const loading = el('p', {}, [el('span', { class: 'device-finder__loading', 'aria-hidden': 'true' }), document.createTextNode(S.loading)]);
    status.append(heading, el('p', { html: S.chooseSize }), loading);

    const settled = await Promise.allSettled((device.handles || []).filter(Boolean).map(fetchCard));
    if (run !== runId) return;
    const items = settled.filter((s) => s.status === 'fulfilled').map((s) => s.value);
    loading.remove();
    if (!items.length) {
      status.append(el('p', { text: S.doubleCheck }));
      return;
    }
    status.append(el('h3', { text: S.productsAvailable }), el('p', { html: S.buyConfidence }));
    // Same rhythm as collection grids: a lifestyle tile after the 4th card, then every 7, at most 3.
    let nextSlot = 4;
    let tiles = 0;
    items.forEach(({ card, life }, i) => {
      grid.append(document.importNode(card, true));
      if (life && i + 1 >= nextSlot && tiles < 3) {
        grid.append(document.importNode(life, true));
        tiles += 1;
        nextSlot = i + 1 + 7;
      }
    });
    if (T.reveal) T.reveal(grid);
    T.parallax?.(grid);
    results.hidden = false;
  }

  form.addEventListener('submit', (e) => {
    e.preventDefault();
    search(input.value);
  });

  const initial = new URL(location.href).searchParams.get('model');
  if (initial) {
    input.value = initial;
    search(initial);
  }
})();
